import {useInfiniteQuery} from 'react-query'
import {useRouter} from 'next/router'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import type {FormEvent, MouseEvent} from 'react'
import {useContext, useEffect, useMemo, useState} from 'react'
import {Pagination} from '@/products/flexible-web-toolkit/components/pagination'
import {Button} from '@/products/flexible-web-toolkit/components/button/button'
import {SkeletonLoader} from '@/products/flexible-web-toolkit/components/skeleton-loader'
import {pushToDataLayer} from '@/utilities/analytics'
import type {
	Block,
	TaxonomySpeciesContentItem,
	Tersed,
} from '@/services/kontent-ai/types'
import {fetchListerItems} from '@/services/lister-items/client'
import type {ListerItemsQueryKey} from '@/products/flexible-web-toolkit/blocks/module-coupon-lister/types'
import {
	EnhancedProductDirectoryCardBlock,
	type EnhancedProductDirectoryCardContentItem,
} from '@/products/flexible-web-toolkit/blocks/enhanced-product-card'
import {GlobalContext} from '@/services/global-client-state'
import {useAuth} from '@/products/auth/auth-provider'
import {textChecker} from '@/utilities/rich-text'
import type {GlobalState} from '@/models/global-state.ts'
import {ListerSorting} from '../lister-sorting'
import {dropDownItems, sortItems} from '../lister-helper'
import type {ListerSortingContentItem, SortingCriterion} from '../lister-model'

export type ProductDirectoryListerContentItem = IContentItem<{
	initialItemsNumber: Elements.NumberElement
	subsequentItemsNumber: Elements.NumberElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	usePagination: Elements.MultipleChoiceElement
	paginationString: Elements.TextElement
	buttonText: Elements.TextElement
	nextButtonText: Elements.TextElement
	prevButtonText: Elements.TextElement
	itemsOrderingOptions: Elements.MultipleChoiceElement
	productDirectoryListerSorting: Elements.LinkedItemsElement<ListerSortingContentItem>
}>

function buildProductFilters(
	speciesCodenames: string[],
	productsFilterOptions: GlobalState['productsFilterOptions']
): {taxonomy: string; terms: string[]}[] {
	const speciesFilter = [
		{
			taxonomy: 'species',
			terms: speciesCodenames,
		},
	]
		.map((item) => ({
			...item,
			terms: item.terms.filter(Boolean),
		}))
		.filter((item) => item.terms.length > 0)

	const updatedFilters = Object.entries(productsFilterOptions).map(
		([taxonomy, options]) => ({
			taxonomy,
			terms: (() => {
				if ('checkboxOptions' in options) {
					return options.checkboxOptions
						.filter((opt) => opt.checked)
						.map((opt) => opt.codename)
				}
				return [options.value]
			})(),
		})
	)

	return [...speciesFilter, ...updatedFilters]
}

function isModifiedPrimaryClick(
	event:
		| MouseEvent<HTMLAnchorElement | HTMLButtonElement>
		| FormEvent<HTMLFormElement>
): boolean {
	return (
		'button' in event &&
		(event.button !== 0 ||
			event.metaKey ||
			event.altKey ||
			event.ctrlKey ||
			event.shiftKey)
	)
}

export const ProductDirectoryListerBlock: Block<
	ProductDirectoryListerContentItem
> = ({block, page, globalConfig, ...context}) => {
	const serverRenderedBlock = block as typeof block & {
		initialItems?: Tersed<EnhancedProductDirectoryCardContentItem>[]
		initialTotalCount?: number
	}
	const router = useRouter()
	const gs = useContext(GlobalContext)
	const authState = useAuth()

	const {page: currentPage} = router.query
	const serverRenderedInitialItems = serverRenderedBlock.initialItems

	const [sortingCriterion, setSortingCriterion] = useState<
		SortingCriterion | undefined
	>()

	const {buttonText} = block.elements

	const nextButtonText =
		'nextButtonText' in block.elements && block.elements.nextButtonText
			? block.elements.nextButtonText
			: 'Next'

	const prevButtonText =
		'prevButtonText' in block.elements && block.elements.prevButtonText
			? block.elements.prevButtonText
			: 'Prev'

	const subsequentArticlesNumber = block.elements.subsequentItemsNumber ?? 6

	const initialItemsNumber = block.elements.initialItemsNumber ?? 6

	const [limitNumberWithSubSeq, setLimitNumberWithSubSeq] =
		useState(initialItemsNumber)

	const speciesCodenames = useMemo(
		() =>
			'filterByTaxonomy' in page.elements
				? (
						page.elements
							.filterByTaxonomy as Tersed<TaxonomySpeciesContentItem>[]
					).flatMap((item) =>
						item.elements.species.map((species) => species.codename)
					)
				: [],
		[page.elements]
	)

	const [filters, setFilters] = useState<
		{
			taxonomy: string
			terms: string[]
		}[]
	>(
		buildProductFilters(
			speciesCodenames,
			gs?.[0]?.productsFilterOptions ?? {}
		)
	)

	useEffect(() => {
		setFilters(
			buildProductFilters(
				speciesCodenames,
				gs?.[0]?.productsFilterOptions ?? {}
			)
		)
		setLimitNumberWithSubSeq(initialItemsNumber)
	}, [gs, initialItemsNumber, speciesCodenames])

	const usePagination = block.elements.usePagination[0]?.codename === 'yes'
	const userRoles = authState.userDetails.userRoles ?? []

	const {isLoading, isFetching, fetchNextPage, data} = useInfiniteQuery({
		queryKey: [
			'productDirectoryLister',
			{
				codename: 'enhanced_product_directory_card',
				locale: router.locale ?? '',
				filters: JSON.stringify(filters),
				orderBy:
					block.elements.itemsOrderingOptions[0]?.codename ??
					undefined,
				hideElementCodename: 'hide_from_lister',
				skip:
					(Number(currentPage?.toString() ?? 1) - 1) *
					initialItemsNumber,
				limit: limitNumberWithSubSeq,
				roles: userRoles.join(','),
			},
		] as ListerItemsQueryKey,
		queryFn: (opts) =>
			fetchListerItems<Tersed<EnhancedProductDirectoryCardContentItem>>(
				opts
			),
		staleTime: 10 * (60 * 1000),
		cacheTime: 15 * (60 * 1000),
		enabled: globalConfig.elements.cdcKey
			? (authState.ready && !authState.authenticated) ||
				(authState.ready &&
					authState.authenticated &&
					Boolean(authState.userDetails.id))
			: true,
	})

	const fetchedProductCards = useMemo(
		() => data?.pages.flatMap((item) => item.items),
		[data]
	)
	const displayedProductCards = useMemo(
		() =>
			sortItems<Tersed<EnhancedProductDirectoryCardContentItem>>(
				[...(fetchedProductCards ?? serverRenderedInitialItems ?? [])],
				sortingCriterion,
				router.locale
			),
		[
			fetchedProductCards,
			router.locale,
			serverRenderedInitialItems,
			sortingCriterion,
		]
	)

	const knownTotalCount =
		data?.pages[0]?.pagination.totalCount ??
		serverRenderedBlock.initialTotalCount
	const totalCount =
		knownTotalCount ?? serverRenderedInitialItems?.length ?? 0
	const activePageNumber = Number(currentPage) || 1
	const nextCrawlerPageNumber = activePageNumber + 1
	const paginationPageCount =
		knownTotalCount === undefined &&
		displayedProductCards.length >= initialItemsNumber
			? nextCrawlerPageNumber
			: Math.ceil(totalCount / initialItemsNumber)
	const shouldShowInitialSkeleton =
		!data && displayedProductCards.length === 0
	const isAwaitingAuthenticatedHydration =
		authState.authenticated &&
		(!authState.ready || !authState.userDetails.id || isFetching)
	const shouldShowAuthenticatedRefreshSkeleton =
		isAwaitingAuthenticatedHydration &&
		!data &&
		(serverRenderedInitialItems?.length ?? 0) > 0
	const shouldShowFetchingSkeleton =
		isFetching && Boolean(data) && displayedProductCards.length > 0
	const shouldShowProductCards =
		((!isLoading && data) || displayedProductCards.length > 0) &&
		!shouldShowAuthenticatedRefreshSkeleton

	function getDisplayedItemCount(): number {
		return displayedProductCards.length
	}

	function getHasNextPage(): boolean {
		const displayedItems = getDisplayedItemCount()

		if (knownTotalCount === undefined) {
			return displayedItems >= initialItemsNumber
		}

		return displayedItems < knownTotalCount
	}

	const handleSortingLister = (
		e: React.ChangeEvent<HTMLSelectElement>
	): void => {
		setSortingCriterion(e.target.value as SortingCriterion)
	}

	const onPageChange = (nextPage: number): void => {
		const currentQuery = new URLSearchParams(
			router.asPath.split('?')[1] || ''
		)
		currentQuery.set('page', nextPage.toString())

		void router.push({
			pathname: `${router.locale ?? ''}${router.asPath.split('?')[0]}`,
			query: currentQuery.toString(),
		})
	}

	return (
		<div>
			{shouldShowInitialSkeleton ||
			shouldShowAuthenticatedRefreshSkeleton ? (
				<div className="mx-auto my-2 grid max-w-screen-xl grid-cols-1 gap-10 px-3 sm:grid-cols-2 sm:px-0 md:grid-cols-3">
					<SkeletonLoader amount={initialItemsNumber} />
				</div>
			) : null}
			{shouldShowProductCards ? (
				<div className="mx-auto flex max-w-screen-xl flex-wrap items-center justify-between">
					<p
						className="mb-4 px-4 text-center font-bold sm:text-left"
						data-kontent-element-codename="pagination_string"
					>
						{block.elements.paginationString
							.replaceAll(
								'{{X}}',
								getDisplayedItemCount().toString()
							)
							.replaceAll('{{Y}}', totalCount.toString())}
					</p>
					{block.elements.productDirectoryListerSorting[0] ? (
						<ListerSorting
							className="w-full px-4 sm:w-auto"
							codename={
								block.elements.productDirectoryListerSorting[0]
									?.system.codename
							}
							dropDownData={dropDownItems(
								block.elements.productDirectoryListerSorting[0]
							)}
							id={
								block.elements.productDirectoryListerSorting[0]
									?.system.id || 'sorting_product_listers'
							}
							label={textChecker(
								block.elements.productDirectoryListerSorting[0]
									?.elements.defaultSortingText.value || '',
								'Sort By'
							)}
							onChange={handleSortingLister}
						/>
					) : null}
				</div>
			) : null}
			{shouldShowProductCards ? (
				<div className="mx-auto flex max-w-screen-xl flex-wrap justify-center">
					{displayedProductCards
						.filter(authState.checkUserRoleForBlock)
						.map((item) => (
							<div
								className="my-2 flex w-full flex-wrap justify-center sm:w-1/2 md:w-1/3"
								key={item.system.id}
							>
								<EnhancedProductDirectoryCardBlock
									block={item}
									globalConfig={globalConfig}
									page={page}
									{...context}
								/>
							</div>
						))}
				</div>
			) : null}
			{shouldShowFetchingSkeleton ? (
				<div className="mx-auto my-2 grid max-w-screen-xl grid-cols-1 gap-10 px-3 sm:grid-cols-2 sm:px-0 md:grid-cols-3">
					<SkeletonLoader amount={subsequentArticlesNumber} />
				</div>
			) : null}
			{usePagination && paginationPageCount > 0 ? (
				<Pagination
					activeIndex={Number(currentPage) || 1}
					amountOfPages={paginationPageCount}
					nextButtonText={<span>{nextButtonText}</span>}
					onPageChange={onPageChange}
					pagesToShow={3}
					prevButtonText={<span>{prevButtonText}</span>}
					showLastPage
				/>
			) : (
				buttonText &&
				getHasNextPage() && (
					<div
						className="my-10 text-center"
						data-kontent-element-codename="button_text"
					>
						<Button
							href={`?page=${nextCrawlerPageNumber}`}
							onClick={async (event) => {
								if (isModifiedPrimaryClick(event)) {
									return
								}

								event.preventDefault()
								setLimitNumberWithSubSeq(
									limitNumberWithSubSeq +
										subsequentArticlesNumber
								)
								await fetchNextPage()
								pushToDataLayer({
									event: 'cta_click',
									cta_name: buttonText,
									cta_category: 'button',
								})
							}}
							type="button"
						>
							{buttonText}
						</Button>
					</div>
				)
			)}
		</div>
	)
}
